import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchAirportSuggestions, searchAirports } from '../api/flights.api';
import type { Airport } from '../api/flights.types';

export const MIN_AIRPORT_QUERY_LENGTH = 3;
const DEBOUNCE_MS = 350;

export interface UseAirportOptionsResult {
  origins: Airport[];
  destinations: Airport[];
  isLoadingOrigins: boolean;
  isLoadingDestinations: boolean;
  /** Feed every keystroke here; short terms fall back to the default suggestions. */
  search: (term: string, field: 'origin' | 'destination') => void;
}

interface FieldState {
  options: Airport[];
  isLoading: boolean;
}

const IDLE: FieldState = { isLoading: false, options: [] };

/**
 * Airport typeahead for the flights form: default suggestions on mount, then
 * debounced remote search once the term is long enough. Stale responses are
 * discarded so a slow request can never overwrite a newer term's results.
 */
export function useAirportOptions(): UseAirportOptionsResult {
  const [defaults, setDefaults] = useState<{ origin: Airport[]; destination: Airport[] }>({
    destination: [],
    origin: [],
  });
  const [origin, setOrigin] = useState<FieldState>(IDLE);
  const [destination, setDestination] = useState<FieldState>(IDLE);

  const latestTermRef = useRef<{ origin: string; destination: string }>({
    destination: '',
    origin: '',
  });
  const timersRef = useRef<{ origin?: number; destination?: number }>({});

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    fetchAirportSuggestions(controller.signal)
      .then((result) => {
        if (!active) return;
        setDefaults(result);
        setOrigin((prev) =>
          latestTermRef.current.origin ? prev : { isLoading: false, options: result.origin },
        );
        setDestination((prev) =>
          latestTermRef.current.destination
            ? prev
            : { isLoading: false, options: result.destination },
        );
      })
      .catch(() => {
        if (active) setDefaults({ destination: [], origin: [] });
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, []);

  useEffect(
    () => () => {
      window.clearTimeout(timersRef.current.origin);
      window.clearTimeout(timersRef.current.destination);
    },
    [],
  );

  const search = useCallback(
    (term: string, field: 'origin' | 'destination') => {
      const setState = field === 'origin' ? setOrigin : setDestination;
      const trimmed = term.trim();

      latestTermRef.current[field] = trimmed;
      window.clearTimeout(timersRef.current[field]);

      if (trimmed.length === 0) {
        setState({ isLoading: false, options: defaults[field] });
        return;
      }

      if (trimmed.length < MIN_AIRPORT_QUERY_LENGTH) {
        setState({ isLoading: false, options: [] });
        return;
      }

      setState((prev) => ({ ...prev, isLoading: true }));

      timersRef.current[field] = window.setTimeout(() => {
        searchAirports(trimmed)
          .then((options) => {
            if (latestTermRef.current[field] !== trimmed) return;
            setState({ isLoading: false, options });
          })
          .catch(() => {
            if (latestTermRef.current[field] !== trimmed) return;
            setState({ isLoading: false, options: [] });
          });
      }, DEBOUNCE_MS);
    },
    [defaults],
  );

  return {
    destinations: destination.options,
    isLoadingDestinations: destination.isLoading,
    isLoadingOrigins: origin.isLoading,
    origins: origin.options,
    search,
  };
}
